import type { Query } from '@rover/utilities/query';

import TrainingUrls from '../constants/urls';
import {
  encodeApiQueryParamsForPageUrl,
  TrainerSearchPageApiQueryParams,
} from '../TrainerSearchPage/params';

export { parseCoordinate } from '../TrainerSearchPage/utilities';

export type SkillsAndBehaviorSlugsDictionary = {
  skillsSlugs: string[];
  behaviorsSlugs: string[];
};

type SkillsAndBehaviorParamDictionary = {
  skills: string;
  behaviors: string;
};

export const parseToArray = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return String(value).split(',').filter(Boolean);
};

export const encodeSkillAndBehaviorQueryParams = ({
  skillsSlugs,
  behaviorsSlugs,
}: SkillsAndBehaviorSlugsDictionary): SkillsAndBehaviorParamDictionary => {
  const skills = skillsSlugs.join(',');
  const behaviors = behaviorsSlugs.join(',');
  return { skills, behaviors };
};

export const setSkillAndBehaviorQueryParams = (
  searchParams: URLSearchParams,
  { skillsSlugs = [], behaviorsSlugs = [] }: Partial<SkillsAndBehaviorSlugsDictionary>
): URLSearchParams => {
  const { skills, behaviors } = encodeSkillAndBehaviorQueryParams({ skillsSlugs, behaviorsSlugs });
  if (skills) {
    searchParams.set('skills', skills);
  }
  if (behaviors) {
    searchParams.set('behaviors', behaviors);
  }
  return searchParams;
};

export const decodeSkillAndBehaviorQueryParams = (
  paramString: string
): SkillsAndBehaviorSlugsDictionary => {
  const params = new URLSearchParams(paramString);
  const skills = params.get('skills');
  const behaviors = params.get('behaviors');
  const skillsSlugs = skills ? skills.split(',') : [];
  const behaviorsSlugs = behaviors ? behaviors.split(',') : [];
  return { skillsSlugs, behaviorsSlugs };
};

export const decodeSkillAndBehaviorFromQuery = (query: Query): SkillsAndBehaviorSlugsDictionary => {
  const skillsSlugs = parseToArray(query.skills);
  const behaviorsSlugs = parseToArray(query.behaviors);

  return { skillsSlugs, behaviorsSlugs };
};

/**
 * Everything that can be serialized into query params when linking to the trainer
 * search page URL.
 */
export type TrainerSearchPageParams = TrainerSearchPageApiQueryParams & {
  skills?: string[];
  behaviors?: string[];
};

export const encodeTrainerSearchPageParams = (params: TrainerSearchPageParams): URLSearchParams => {
  const searchParams = encodeApiQueryParamsForPageUrl(params);
  return setSkillAndBehaviorQueryParams(searchParams, {
    skillsSlugs: params.skills,
    behaviorsSlugs: params.behaviors,
  });
};

/**
 * Builds the URL for navigating to the trainer search page from any other page,
 * optionally seeding it with location filters and skills/behaviors selections.
 * Returns the bare page URL when no params apply.
 */
export const getTrainerSearchPageUrl = (params: TrainerSearchPageParams = {}): string => {
  const queryString = encodeTrainerSearchPageParams(params).toString();
  return queryString ? `${TrainingUrls.SEARCH}?${queryString}` : TrainingUrls.SEARCH;
};
